import React from "react";
import moment from "moment";
import { Alert } from "react-bootstrap";
import { useFormValidation } from "./form-validation-context";
import { messageCategoryOptions, messagePriorityOptions, messageTypeOptions } from "./form-select-options";
import { GridMessageFilters } from "../../../types/MessageType";
import { Controller, useFormContext } from "react-hook-form";
import { DateInput, FormCheck, FormInput, FormLabel, FormSelect } from "../../forms";
import { MasterAccountSelect, MessageUserDelegatesSelect, UserDelegatesSelect } from "../../forms/selects";

export const MessageFilterForm = ({ isClientLevel = true } : Readonly<{ isClientLevel: boolean }>) => {
    const { setIsValid } = useFormValidation();
    const { control, setValue, watch } = useFormContext<GridMessageFilters>();
    const form = watch();
    const recipients = watch('recipientId');
    const sentDateTo = watch('sentDateTo');
    const sentDateFrom = watch('sentDateFrom');
    const masterAccountId = watch('masterAccountId');

    const applyFilterLimitations = React.useMemo(() => {
        const { masterAccountId, recipientId, senderId, showDone, showHiddenByUser } = form;    
        return showDone
            && showHiddenByUser
            && !masterAccountId
            && (recipientId)
            && (senderId);
    }, [ form ]);

    React.useEffect(() => {
        if (!applyFilterLimitations || !sentDateFrom) {
            return;
        }

        var from = moment(sentDateFrom);
        var to = moment(sentDateTo);
        
        if (!to.isValid() || Math.abs(from.diff(to, "d")) > 60) {
            from.add(60, "d");
            setValue("sentDateTo", from.toDate().toString(), { shouldDirty: true });
        }
    }, [ applyFilterLimitations, sentDateFrom ]);

    React.useEffect(() => {
        if (!applyFilterLimitations || !sentDateTo) {
            return;
        }

        var from = moment(sentDateFrom);
        var to = moment(sentDateTo);

        if (!from.isValid() || Math.abs(from.diff(to, "d")) > 60) {

            to.add(-60, "d");
            setValue("sentDateFrom", to.toDate().toString(), { shouldDirty: true });
        }
    }, [ applyFilterLimitations, sentDateTo ]);

    React.useEffect(() => {
        if (!applyFilterLimitations) {
            return;
        }

        var to = moment(sentDateTo);
        var from = moment(sentDateFrom);
        setIsValid(Math.abs(from.diff(to, "d")) <= 60);
    }, [ applyFilterLimitations, sentDateFrom, sentDateTo ]);

    // React.useEffect(() => {
    //     if (recipients && recipients.length > 1) {
    //         setValue('showDone', false, { shouldDirty: true });
    //         setValue('showHiddenByUser', false, { shouldDirty: true });
    //     }
    // }, [ recipients ]);

    return (
        <div className="row gy-2">
            <div className="col-6">
                <Controller
                    name="masterAccountId"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <MasterAccountSelect
                            label="Master Account"
                            isMulti={false}
                            isClearable={true}
                            disableAnimations
                            value={value}
                            onChange={(opt: SelectOption<string> | null) => setValue('masterAccountId', opt?.value, { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            <div className="col-6">
                <Controller
                    name="recipientId"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <MessageUserDelegatesSelect
                            masterAccountId={masterAccountId}
                            disableAnimations
                            label="Recipient"
                            isMulti={false}
                            defaultValue={value}
                            isClearable={false}
                            onChange={(opt: SelectOption<string>) => setValue('recipientId', opt?.value, { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            <div className="col-6">
                <Controller
                    name="senderId"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <MessageUserDelegatesSelect
                            masterAccountId={masterAccountId}
                            disableAnimations
                            label="Sender"
                            isMulti={false}
                            defaultValue={value}
                            onChange={(opt: SelectOption<string>) => setValue('senderId', opt?.value, { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            <div className="col-6 d-flex align-items-end">
                <Controller
                    name="sentDateFrom"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <DateInput
                            label="Sent Date"
                            disableAnimations
                            isClearable={!applyFilterLimitations}
                            value={value}
                            onBlur={(v: string | null) => setValue('sentDateFrom', v, { shouldDirty: true })}
                        />
                    )}
                />
                <FormLabel className={"mx-2"}>to</FormLabel>
                <Controller
                    name="sentDateTo"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <DateInput
                            disableAnimations
                            isClearable={!applyFilterLimitations}
                            value={value}
                            onBlur={(v: string | null) => setValue('sentDateTo', v, { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            <div className="col-6">
                <Controller
                    name="subject"
                    control={control}
                    render={({ field: { ref, value, ...rest } }) => (
                        <FormInput
                            label="Subject"
                            disableAnimations
                            value={value}
                            {...rest}
                        />
                    )}
                />
            </div>
            <div className="col-6 d-flex justify-content-between align-items-end pb-2">
                <Controller
                    name="showDone"
                    control={control}
                    render={({ field: { ref, value, ...rest } }) => (
                        <FormCheck
                            label={"Show Done"}
                            disableAnimations
                            isChecked={value}
                            onChange={(value: boolean) => setValue('showDone', value, { shouldDirty: true })}
                        />
                    )}
                />
                <Controller
                    name="showHiddenByUser"
                    control={control}
                    render={({ field: { ref, value, ...rest } }) => (
                        <FormCheck
                            label={"Show Hidden"}
                            disableAnimations
                            isChecked={value}
                            onChange={(value: boolean) => setValue('showHiddenByUser', value, { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            <div className="col-6">
                <Controller
                    name="type"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <FormSelect
                            label="Message Type"
                            isMulti={false}
                            disableAnimations
                            options={messageTypeOptions}
                            defaultValue={value}
                            onChange={(v: SelectOption<number>) => setValue("type", v.value, { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            <div className="col-6">
                <Controller
                    name="category"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <FormSelect
                            label="Message Category"
                            isMulti={false}
                            disableAnimations
                            options={messageCategoryOptions}
                            defaultValue={value}
                            onChange={(v: SelectOption<number>) => setValue("category", v.value, { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            <div className="col-6">
                <Controller
                    name="priorities"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <FormSelect
                            label="Message Priority"
                            isMulti={true}
                            disableAnimations
                            options={messagePriorityOptions}
                            defaultValue={value}
                            onChange={(v: SelectOption<number>[]) => setValue("priorities", v.map(e => e.value), { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            <div className="col-6 d-flex align-items-end">
                <Controller
                    name="deadlineDateFrom"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <DateInput
                            label="Deadline Date"
                            disableAnimations
                            isClearable={true}
                            value={value}
                            onBlur={(v: string | null) => setValue('deadlineDateFrom', v, { shouldDirty: true })}
                        />
                    )}
                />
                <FormLabel className={"mx-3"}>to</FormLabel>
                <Controller
                    name="deadlineDateTo"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <DateInput
                            disableAnimations
                            isClearable={true}
                            value={value}
                            onBlur={(v: string | null) => setValue('deadlineDateTo', v, { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            <div className="col-6">
                <Controller
                    name="userPriorities"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <FormSelect
                            label="User Priority"
                            isMulti={true}
                            disableAnimations
                            options={messagePriorityOptions}
                            defaultValue={value}
                            onChange={(v: SelectOption<number>[]) => setValue("userPriorities", v.map(e => e.value), { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            <div className="col-6 d-flex align-items-end">
                <Controller
                    name="reminderDateFrom"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <DateInput
                            label="Reminder Date"
                            disableAnimations
                            isClearable={true}
                            value={value}
                            onBlur={(v: string | null) => setValue('reminderDateFrom', v, { shouldDirty: true })}
                        />
                    )}
                />
                <FormLabel className={"mx-3"}>to</FormLabel>
                <Controller
                    name="reminderDateTo"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <DateInput
                            disableAnimations
                            isClearable={true}
                            value={value}
                            onBlur={(v: string | null) => setValue('reminderDateTo', v, { shouldDirty: true })}
                        />
                    )}
                />
            </div>
            {applyFilterLimitations && (
                <div className="col-12">
                    <Alert variant="warning">
                        <span>
                            You must select a 'Sent Date' range of 60 days or less in order to view done messages with no filters on accounts, senders or recipients.
                        </span>
                    </Alert>
                </div>
            )}
        </div>
    );
};